import { Coffee, TrendingUp, Clock, CheckCircle, ArrowRight, Star, ShieldCheck, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const LOGO_URL = "https://img1.wsimg.com/isteam/ip/d9f026ae-fdac-4ce0-8e76-8f2f65f8f36d/CafeSales%20Logo.png/:/rs=h:139,cg:true,m/qt=q:95";

const LandingScreen = ({ onStart }) => {
    const benefits = [
        { icon: TrendingUp, text: "Used by café owners across Australia" },
        { icon: CheckCircle, text: "Based on real café sales data" },
        { icon: Clock, text: "Takes less than 3 minutes" }
    ];

    return (
        <div className="min-h-screen hero-gradient">
            {/* Header */}
            <header className="w-full py-4 px-6 lg:px-12">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link 
                            to="/" 
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:text-primary hover:bg-gray-100 transition-colors"
                            data-testid="back-home-link"
                        >
                            <Home className="w-4 h-4" />
                            <span className="text-sm font-medium hidden sm:inline">Home</span>
                        </Link>
                        <img 
                            src={LOGO_URL} 
                            alt="Cafe Sales Experts" 
                            className="h-12 md:h-16 object-contain"
                            data-testid="cafe-sales-logo"
                        />
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm text-text-secondary">
                        <ShieldCheck className="w-4 h-4 text-green-600" />
                        <span>Trusted by 2,000+ café owners</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 lg:px-12 pt-8 pb-20">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    {/* Left Column - Text Content */}
                    <motion.div 
                        className="space-y-8"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                <Coffee className="w-4 h-4" />
                                <span>Café Valuation Tool</span>
                            </div>
                            
                            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                                What Is Your{" "}
                                <span className="gradient-text">Café Worth?</span>
                            </h1>
                            
                            <p className="text-lg text-text-secondary max-w-lg">
                                Get an instant café valuation based on real café sales data 
                                and industry benchmarks. Know your business value before you sell.
                            </p>
                        </div>

                        {/* Benefits */}
                        <div className="space-y-4">
                            {benefits.map((benefit, index) => (
                                <motion.div
                                    key={index}
                                    className="flex items-center gap-3"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                                >
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                                        <benefit.icon className="w-5 h-5 text-secondary" />
                                    </div>
                                    <span className="text-text-primary font-medium">{benefit.text}</span>
                                </motion.div>
                            ))}
                        </div>

                        {/* CTA Button */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.5 }}
                        >
                            <Button
                                onClick={onStart}
                                size="lg"
                                className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 transition-all duration-300 transform hover:-translate-y-1 rounded-xl px-8 py-6 text-lg font-semibold group cta-pulse"
                                data-testid="start-valuation-btn"
                            >
                                Start My Café Valuation
                                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Button>
                            <p className="text-sm text-text-muted mt-3">
                                Free • No obligation • Results in under 3 minutes
                            </p>
                        </motion.div>

                        {/* Trust Indicators */}
                        <div className="flex items-center gap-6 pt-4">
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                ))}
                            </div>
                            <span className="text-sm text-text-secondary">
                                4.8 rating from 476 reviews
                            </span>
                        </div>
                    </motion.div>

                    {/* Right Column - Image */}
                    <motion.div
                        className="relative"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <div className="relative rounded-2xl overflow-hidden shadow-float">
                            <img
                                src="https://images.pexels.com/photos/1995010/pexels-photo-1995010.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Modern café interior"
                                className="w-full h-[400px] lg:h-[500px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                            
                            {/* Floating Stats Card */}
                            <motion.div
                                className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.8 }}
                            >
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <p className="text-2xl font-bold text-primary">900+</p>
                                        <p className="text-xs text-text-muted">Businesses Sold</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-primary">$10M+</p>
                                        <p className="text-xs text-text-muted">Commissions Saved</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-primary">4%</p>
                                        <p className="text-xs text-text-muted">Fair Commission</p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Decorative Elements */}
                        <div className="absolute -top-4 -right-4 w-24 h-24 bg-secondary/20 rounded-full blur-2xl" />
                        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />
                    </motion.div>
                </div>
            </main>

            {/* Footer */}
            <footer className="w-full py-6 px-6 border-t border-gray-100">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-text-muted">
                    <p>© 2026 Cafe Sales Experts. All rights reserved.</p>
                    <div className="flex items-center gap-6">
                        <Link to="/" className="hover:text-primary transition-colors">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingScreen;
